'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { GoogleIcon } from '@/components/icons/google-icon';
import { Button } from '@/components/ui/button';
import { signInWithOAuth } from '@/app/actions/auth';
import { getSafeRedirectPath } from '@/utils/redirect-validation';

interface GoogleAuthButtonProps {
  mode?: 'signin' | 'signup';
}

export function GoogleAuthButton({ mode = 'signin' }: GoogleAuthButtonProps) {
  const [loading, setLoading] = useState(false);
  const searchParams = useSearchParams();

  const handleGoogleSignIn = async () => {
    setLoading(true);

    try {
      const returnTo = searchParams.get('returnTo');
      const redirectPath = getSafeRedirectPath(returnTo);

      const { url, error } = await signInWithOAuth({
        provider: 'google',
        redirectTo: redirectPath,
      });

      if (error) {
        console.error('OAuth initiation failed:', error);
        toast.error('Unable to sign in with Google. Please try again.');
        setLoading(false);
        return;
      }

      if (!url) {
        console.error('OAuth URL missing in response');
        toast.error('Unable to sign in with Google. Please try again.');
        setLoading(false);
        return;
      }

      window.location.href = url;
    } catch (error) {
      console.error('Failed to initiate Google sign in:', error);
      toast.error('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      onClick={handleGoogleSignIn}
      isLoading={loading}
      disabled={loading}
    >
      {!loading && <GoogleIcon className="mr-2 h-5 w-5" />}
      {mode === 'signin' ? 'Sign in with Google' : 'Sign up with Google'}
    </Button>
  );
}
